/**
 * Compress an image File on the client before upload.
 *
 * iPhone/Android photos are typically 3–12 MB JPEGs. Production ingress
 * proxies (Kubernetes, Cloudflare, Bluehost, etc.) commonly cap multipart
 * bodies at 1 MB which is why uploads silently fail in prod even though
 * preview works fine. Resizing client-side keeps every upload under ~500 KB
 * while preserving plenty of resolution for hero/hall imagery.
 *
 * Args:
 *   file: the File the user selected
 *   maxDim: the longer edge in pixels (default 1600 — sharp on retina)
 *   quality: 0..1 JPEG quality (default 0.85 — visually lossless for photos)
 *
 * Falls back to the original file untouched for:
 *   - non-image MIME types (PDFs etc.)
 *   - GIFs (preserves animation)
 *   - tiny files already under 400 KB
 *   - any decode failure (so a broken canvas never blocks an upload)
 */
export async function compressImage(file, { maxDim = 1600, quality = 0.85 } = {}) {
  if (!file) return file;
  if (!file.type?.startsWith("image/")) return file;
  if (file.type === "image/gif") return file; // keep animation
  if (file.size < 400 * 1024) return file;

  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });

    const img = await new Promise((resolve, reject) => {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = dataUrl;
    });

    const { naturalWidth: w, naturalHeight: h } = img;
    const longest = Math.max(w, h);
    if (longest <= maxDim) {
      // Already small enough — only re-encode if the original is huge
      if (file.size < 1.5 * 1024 * 1024) return file;
    }
    const scale = Math.min(1, maxDim / longest);
    const tw = Math.round(w * scale);
    const th = Math.round(h * scale);

    const canvas = document.createElement("canvas");
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, tw, th);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) return file;

    // If the encoder somehow produced a bigger file, keep the original.
    if (blob.size >= file.size) return file;

    const newName = (file.name || "image").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], newName, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    // Compression is a best-effort optimisation — never block an upload over it
    return file;
  }
}
